import { useCallback, useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { BotIcon, Loader2Icon, SparklesIcon, HistoryIcon } from 'lucide-react';

import { AISearch, AIConversation, AIMessage } from '@/api/ai-search';
import { getAISearchDocPreviewUrl } from '@/api/ai-search-docs';
import { useConversationsHistory } from '@/hooks/api/use-get-ai-search-conversations';
import { useMessages } from '@/hooks/api/use-get-ai-search-messages';
import { toast_error } from '@/libs/toast-wrappers';

import { BasicDrawer } from '@/components/ui/basic-drawer';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Overlay } from '@/components/ui/overlay';
import { Textarea } from '@/components/form/textarea';

import { AIMessageBubble } from './ai-message-bubble';

export function AISearchAgent() {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState<AIConversation | undefined>(undefined);
  const [streamingMessage, setStreamingMessage] = useState('');

  const streamingMessages = useMemo(
    () => [
      t('ai-search-streaming-1'),
      t('ai-search-streaming-2'),
      t('ai-search-streaming-3'),
      t('ai-search-streaming-4'),
      t('ai-search-streaming-5'),
      t('ai-search-streaming-6'),
      t('ai-search-streaming-7'),
      t('ai-search-streaming-8'),
      t('ai-search-streaming-9'),
    ],
    [t]
  );

  // Selected filename for preview. We use SWR to cache and dedupe presigned URL fetches.
  const [previewFile, setPreviewFile] = useState<string | null>(null);

  const swrResult = useSWR<string, Error>(
    previewFile ? ['ai-search-doc-preview', previewFile] : null,
    async ([, fileName]: [string, string]) => getAISearchDocPreviewUrl(fileName, 3600),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      dedupingInterval: 5 * 60 * 1000,
    }
  );

  const {
    data: previewHref,
    isLoading: previewLoading,
    error: previewError,
    mutate: revalidatePreview,
  } = swrResult;

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const { conversations, refetchConversations } = useConversationsHistory();
  const { messages, refetchMessages, loading: messagesLoading } = useMessages(current?.id);

  // Fake streaming effect
  useEffect(() => {
    if (loading) {
      let currentIndex = 0;
      setStreamingMessage(streamingMessages[0]);

      const interval = setInterval(() => {
        currentIndex = currentIndex + 1;
        if (currentIndex < streamingMessages.length) {
          setStreamingMessage(streamingMessages[currentIndex]);
        }
      }, 3000);

      return () => clearInterval(interval);
    } else {
      setStreamingMessage('');
    }
  }, [loading, streamingMessages]);

  const handleSubmit = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await AISearch(query.trim());
      setCurrent(res.conversation);
      await refetchConversations();
      await refetchMessages();
      setQuery('');
    } finally {
      setLoading(false);
    }
  }, [query, refetchConversations, refetchMessages]);

  const handleSelectConversation = useCallback((item: AIConversation) => {
    setCurrent(item);
  }, []);

  const orderedMessages = useMemo<AIMessage[]>(
    () => [...messages].sort((a, b) => a.created_at.localeCompare(b.created_at)),
    [messages]
  );

  const openSource = useCallback(
    (fileName?: string) => {
      if (!fileName) {
        toast_error('Unable to open document link');
        return;
      }
      setPreviewFile(fileName);
      void revalidatePreview();
    },
    [revalidatePreview]
  );

  const closePreview = useCallback(() => {
    setPreviewFile(null);
  }, []);

  return createPortal(
    <>
      <div className="fixed bottom-4 right-4 z-[70]">
        <Button onClick={handleOpen} variant="secondary" size="lg" className="shadow-lg">
          <BotIcon className="size-4" /> {t('ai-search-title')}
        </Button>
      </div>
      <BasicDrawer open={open} onClose={handleClose} title={t('ai-search-title')}>
        <div className="space-y-4 pb-10">
          {!current && (
            <>
              <div className="flex flex-col gap-2">
                <Textarea
                  name="ai-search"
                  placeholder={t('ai-search-placeholder')}
                  value={query}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuery(e.target.value)}
                  className="min-h-24"
                  disabled={loading}
                />
                <Button
                  onClick={() => void handleSubmit()}
                  disabled={loading}
                  className="self-stretch min-w-28"
                >
                  {loading ? (
                    <Loader2Icon className="size-4 animate-spin" />
                  ) : (
                    <SparklesIcon className="size-4" />
                  )}
                  {loading ? streamingMessage : t('ai-search-ask-ai')}
                </Button>
              </div>
              <div className="overflow-auto rounded-md mt-4 p-2 space-y-1">
                {conversations.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('ai-search-no-history')}</p>
                ) : (
                  <div className="ml-1 mb-2 flex gap-2">
                    <HistoryIcon className="size-4" />
                    <span className="text-xs text-muted-foreground">{t('ai-search-history')}</span>
                  </div>
                )}
                {conversations.map((h) => (
                  <Button
                    key={h.id}
                    variant="ghost-muted"
                    onClick={() => handleSelectConversation(h)}
                    className="w-full flex-col gap-0 items-start text-xs p-2"
                  >
                    <span className="block truncate">{h.initial_msj}</span>
                    <span className="block text-[10px] opacity-60">
                      {new Date(h.created_at).toLocaleString()}
                    </span>
                  </Button>
                ))}
              </div>
            </>
          )}
          {current && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <Button size="sm" variant="ghost-muted" onClick={() => setCurrent(undefined)}>
                  ← {t('go-back')}
                </Button>
                <span className="text-[11px] opacity-60">
                  {new Date(current.created_at).toLocaleString()}
                </span>
              </div>
              <div className="space-y-4">
                <div className="space-y-3">
                  {orderedMessages.map((m) => (
                    <AIMessageBubble
                      key={m.id}
                      message={m}
                      onOpenSource={openSource}
                      previewFile={previewFile}
                      previewLoading={previewLoading}
                    />
                  ))}
                  {orderedMessages.length === 0 && !(loading || messagesLoading) && (
                    <p className="text-xs text-muted-foreground">{t('ai-search-no-history')}</p>
                  )}
                  {messagesLoading && (
                    <div className="w-full flex items-center justify-center">
                      <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {!current && <Separator />}
        </div>
        {/* PDF Preview Overlay */}
        <Overlay
          open={Boolean(previewFile)}
          onClose={closePreview}
          allowScroll
          className="modal-overlay flex items-center justify-center p-4"
        >
          {previewFile && (
            <div className="bg-background rounded-md shadow-xl w-[90vw] h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between p-2 border-b gap-2">
                <span className="text-xs font-medium truncate max-w-[70%]">
                  {t('ai-search-document-preview')}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      if (previewHref) window.open(previewHref, '_blank', 'noopener,noreferrer');
                    }}
                    disabled={!previewHref}
                  >
                    {t('open-in-new-tab')}
                  </Button>
                </div>
              </div>
              <div className="flex-1 bg-muted/30">
                {previewLoading && (
                  <div className="w-full h-full flex items-center justify-center">
                    <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                {!previewLoading && previewHref && (
                  <iframe src={previewHref} title="PDF Preview" className="w-full h-full" />
                )}
                {!previewLoading && !previewHref && previewError && (
                  <div className="w-full h-full flex items-center justify-center text-destructive text-xs p-4 text-center">
                    {t('ai-search-no-history') /* fallback message; could add specific error key */}
                  </div>
                )}
              </div>
            </div>
          )}
        </Overlay>
      </BasicDrawer>
    </>,
    document.body
  );
}
